<template>
  <div :class="[divClasses, scrollable ? 'flex min-h-0 flex-1 flex-col' : '']">
    <!--#region top header -->
    <div class="flex shrink-0 items-center justify-end gap-2 pb-2">
      <div v-if="$slots.toolbar" class="mr-auto flex items-center gap-3">
        <slot name="toolbar" />
      </div>
      <!--#region searchbar -->
      <div v-if="search" class="w-full max-w-sm">
        <BaseSearchInput @type-input="filterList" />
      </div>
      <!--#endregion -->

      <!--#region sync button -->
      <BaseButton
        color="secondary"
        :isoutline="true"
        icon="sync"
        :tooltip="$t('button.refresh')"
        scoped-classes="mx-2"
        :title="null"
        @click="$emit('refresh-click')"
      />
      <!--#endregion -->
    </div>

    <!--#endregion -->
    <div :class="scrollable ? 'min-h-0 flex-1 overflow-auto' : ''">
      <table class="table" :class="tableClasses">
        <!--#region header -->
        <thead
          :class="
            scrollable ? 'sticky top-0 z-10 bg-white dark:bg-slate-700' : ''
          "
        >
          <tr :class="theadRowClasses">
            <BaseThComponent
              v-for="column in columns"
              :key="column.field"
              :column="column"
              :th-classes="thClasses"
              :current-sort="currentSort"
              @sort-column="handleSortColumn"
            />
          </tr>
        </thead>
        <!--#endregion -->
        <tbody>
          <!-- Crema: rows being (re)loaded, the previous ones hidden. -->
          <tr v-if="loading">
            <td :colspan="columns.length" class="py-16">
              <TheLoadingComponent size="small" />
            </td>
          </tr>
          <tr
            v-for="(model, index) in loading ? [] : filteredModels"
            :key="index"
            :class="[
              trClasses,
              generateStripedClass(index),
              rowsClickable ? 'cursor-pointer' : ''
            ]"
            @click="$emit('rowClick', model[redirect_to_field])"
          >
            <td
              v-for="(column, index2) in columns"
              :key="index2"
              class="dark:border-slate-600"
              :class="tdClasses"
            >
              <BaseDeleteButton
                v-if="
                  column.type === 'button' && column.metadata.icon === 'times'
                "
                @click="$emit(column.metadata.method, model.id)"
              />
              <BaseButton
                v-else-if="column.type === 'button'"
                :color="column.metadata.type"
                :icon="column.metadata.icon"
                :tooltip="
                  $t(
                    `button.${
                      column.metadata.icon === 'times' ? 'delete' : 'edit'
                    }`
                  )
                "
                :is-outline="column.metadata.outline"
                :title="column.metadata.title"
                @click.stop="$emit(column.metadata.method, model.id)"
              />

              <BaseLabel
                v-else-if="
                  column.type === 'label' && model[column.metadata.value]
                "
                :icon="column.metadata.icon"
                :content="model[column.metadata.value]"
                :color="model[column.metadata.color]"
              />
              <span
                v-else-if="
                  column.type === 'icon' && model[column?.metadata?.icon]
                "
              >
                <FontAwesomeIcon
                  :class="model[column?.metadata?.color]"
                  :icon="['fas', model[column?.metadata?.icon]]"
                  size="lg"
                />
              </span>
              <BaseLabel
                v-else-if="column.type === 'icon' && model['condition']"
                :icon="column.metadata.icon"
                :content="''"
                :color="column.metadata.color"
              />
              <ContentTableCell v-else :model="model" :column="column" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import searchListHelper from '@u/searchListHelper'
import BaseLabel from '@c/addf-package/components/BaseLabel/BaseLabel.vue'
import ContentTableCell from '@c/addf-package/components/BaseTable/ContentTableCell.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseThComponent from '@c/BaseTableComponent/BaseThComponent.vue'
import BaseSearchInput from '@c/addf-package/components/BaseInput/BaseSearchInput.vue'
import TheLoadingComponent from '@c/TheLoadingComponent.vue'

export default {
  name: 'BaseContentTable',
  components: {
    BaseSearchInput,
    BaseThComponent,
    BaseButton,
    ContentTableCell,
    BaseLabel,
    FontAwesomeIcon,
    TheLoadingComponent
  },
  props: {
    /** Crema: shows a loader in place of the rows. */
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    /** Header bar fixed, rows scrolling in the remaining height. */
    scrollable: {
      type: Boolean,
      required: false,
      default: false
    },
    columns: {
      type: Array,
      required: true
    },
    rowClick: {
      type: String,
      required: false,
      default: ''
    },
    redirect_to_field: {
      type: String,
      required: false,
      default: 'id'
    },
    search: {
      type: Boolean,
      default: false
    },
    rows: {
      // je pense que cette props n'est pas utilisée
      type: Array,
      required: false
    },
    modelList: {
      type: Array,
      required: true
    },
    isBordered: {
      type: Boolean,
      required: false,
      default: false
    },
    isHoverable: {
      type: Boolean,
      required: false,
      default: false
    },
    isSmallTable: {
      type: Boolean,
      required: false,
      default: false
    },
    isResponsive: {
      type: Boolean,
      required: false,
      default: false
    },
    isStriped: {
      type: Boolean,
      required: false,
      default: false
    },
    theadRowClasses: {
      type: String,
      required: false,
      default: ''
    }
  },
  emits: ['refresh-click', 'rowClick'],
  data() {
    return {
      searchTerms: '',
      sortDirection: null,
      sortColumn: null,
      sortType: null,
      currentSort: ''
    }
  },
  computed: {
    tdClasses() {
      return this.isBordered ? 'border' : 'border-b'
    },
    thClasses() {
      return this.isBordered ? 'border' : ''
    },
    trClasses() {
      return this.isHoverable ? 'hover:bg-gray-200 dark:hover:bg-gray-600' : ''
    },
    tableClasses() {
      return this.isSmallTable ? 'table--sm' : ''
    },

    /** Rows are clickable when the parent listens to `rowClick`. */
    rowsClickable() {
      return !!this.$.vnode.props?.onRowClick
    },
    divClasses() {
      return this.isResponsive ? 'overflow-x-auto' : ''
    },
    filteredModels() {
      let copyModelList = this.modelList

      if (this.searchTerms.length >= 1) {
        const search = searchListHelper.normalizeSearch(this.searchTerms)
        copyModelList = copyModelList.filter((model) => {
          return this.filtrableColumns.some(
            (val) =>
              model[val] &&
              searchListHelper.normalizeSearch(model[val]).includes(search)
          )
        })
      }

      if (this.sortDirection && this.sortColumn) {
        copyModelList = copyModelList.sort((a, b) => {
          let fa, fb
          if (this.sortType && this.sortType === 'date') {
            fa = a[this.sortColumn]
              ? a[this.sortColumn]['sort_value']
              : this.sortDirection === 'up'
                ? '9999999999999999'
                : this.sortDirection === 'down'
                  ? '00000000000000000'
                  : ''
            fb = b[this.sortColumn]
              ? b[this.sortColumn]['sort_value']
              : this.sortDirection === 'up'
                ? '9999999999999999'
                : this.sortDirection === 'down'
                  ? '00000000000000000'
                  : ''
          } else {
            // Crema: amounts are numbers, compared as such.
            fa = searchListHelper.sortValue(a[this.sortColumn])
            fb = searchListHelper.sortValue(b[this.sortColumn])
          }
          if (fa < fb) {
            return this.sortDirection === 'up' ? -1 : 1
          }
          if (fa > fb) {
            return this.sortDirection === 'up' ? 1 : -1
          }
          return 0
        })
      }
      return copyModelList
    },

    filtrableColumns() {
      return this.columns
        .filter(function (column) {
          // Crema: core-js's internal `has` here (as in FM) matched nothing.
          return Object.hasOwn(column, 'searchable')
        })
        .map((obj) => {
          return obj.field
        })
    }
  },
  methods: {
    generateStripedClass(index) {
      return this.isStriped && index % 2 !== 0
        ? 'bg-gray-200 dark:bg-slate-800'
        : ''
    },
    mRowClick(val) {
      if (this.rowClick !== '') {
        this.$emit(this.rowClick, val)
      }
    },
    filterList(terms) {
      this.searchTerms = terms
    },
    handleSortColumn({ column, direction, type }) {
      this.currentSort = column

      this.sortColumn = column
      this.sortDirection = direction
      this.sortType = type
    }
  }
}
</script>
